import type { ApplicationError } from '~~/core/helpers/result';

/**
 * Interface for $fetch errors structure from Nuxt
 */
interface FetchErrorData {
	message?: string;
	[key: string]: any;
}

interface FetchErrorResponse {
	status?: number;
	statusText?: string;
	_data?: FetchErrorData;
	[key: string]: any;
}

interface FetchError extends Error {
	response?: FetchErrorResponse;
	statusCode?: number;
	statusMessage?: string;
}

/**
 * Type guard to check if error is a FetchError
 */
function isFetchError(error: unknown): error is FetchError {
	return error instanceof Error && 'response' in error;
}

/**
 * Type guard to check if error is an ApplicationError
 */
function isApplicationError(error: unknown): error is ApplicationError {
	return (
		error !== null &&
		typeof error === 'object' &&
		'message' in error &&
		'source' in error &&
		typeof (error as any).message === 'string' &&
		typeof (error as any).source === 'string'
	);
}

/**
 * Extracts status code and message from a FetchError (type-safe)
 * @param error - The FetchError from $fetch
 * @returns Object with statusCode and statusMessage or null
 */
function extractFetchErrorInfo(
	error: FetchError
): { statusCode?: number; statusMessage?: string } | null {
	const statusCode = error.response?.status || error.statusCode;
	const statusMessage =
		error.response?._data?.message ||
		error.statusMessage ||
		error.response?.statusText ||
		error.message;

	if (statusCode) {
		return { statusCode, statusMessage };
	}

	return null;
}

interface OriginalErrorObject {
	statusCode?: number;
	statusMessage?: string;
	name?: string;
	message?: string;
	stack?: string;
	cause?: unknown;
	responseData?: unknown;
}

/**
 * Creates a properly typed error object for repository layer
 * @param error - The caught error (unknown type)
 * @returns Object with statusCode/statusMessage (for ApplicationError compatibility)
 *          plus name/message/stack/cause/responseData so downstream logs can
 *          surface the real cause instead of a stripped-down summary.
 */
function createOriginalErrorObject(error: unknown): OriginalErrorObject {
	const base: OriginalErrorObject = {};

	if (error instanceof Error) {
		base.name = error.name;
		base.message = error.message;
		base.stack = error.stack;
		if ('cause' in error && error.cause !== undefined) {
			base.cause =
				error.cause instanceof Error
					? {
							name: error.cause.name,
							message: error.cause.message,
							stack: error.cause.stack
						}
					: error.cause;
		}
	}

	if (isFetchError(error)) {
		const fetchErrorInfo = extractFetchErrorInfo(error);
		if (fetchErrorInfo) {
			return {
				...base,
				...fetchErrorInfo,
				responseData: error.response?._data
			};
		}
		return {
			...base,
			responseData: error.response?._data
		};
	}

	if (error instanceof Error) {
		return {
			...base,
			statusMessage: error.message
		};
	}

	return {
		...base,
		statusMessage: String(error),
		message: String(error)
	};
}

/**
 * Extracts original error info from ApplicationError originalErrorObject
 * @param error - ApplicationError with originalErrorObject
 * @returns Object with statusCode and statusMessage or null
 */
function extractOriginalErrorInfo(
	error: any
): { statusCode?: number; statusMessage?: string } | null {
	const originalErrorObj = error?.originalErrorObject;

	if (!originalErrorObj || typeof originalErrorObj !== 'object') {
		return null;
	}

	// Check if it has statusCode and statusMessage properties
	if ('statusCode' in originalErrorObj && 'statusMessage' in originalErrorObj) {
		return {
			statusCode: originalErrorObj.statusCode,
			statusMessage: originalErrorObj.statusMessage
		};
	}

	// Legacy support: check if it has response.status structure
	if ('response' in originalErrorObj) {
		const response = (originalErrorObj as any).response;
		return {
			statusCode: response?.status,
			statusMessage: response?._data?.message || response?.statusText
		};
	}

	return null;
}

/**
 * Legacy function for backward compatibility
 * @param error - The ApplicationError
 * @returns Status code or null
 */
function getErrorStatusCode(error: any) {
	return error?.originalErrorObject && 'response' in error.originalErrorObject
		? (error.originalErrorObject.response as any).status
		: null;
}

/**
 * Creates an H3Error from a repository ApplicationError, preserving original status codes and messages
 * @param error - The ApplicationError from repository layer
 * @param fallbackStatusCode - Status code to use if no original status is found (default: 500)
 * @param fallbackMessage - Message to use if no original message is found
 * @returns H3Error with appropriate status code and message
 */
function createErrorFromRepositoryError(
	error: ApplicationError,
	fallbackStatusCode: number = 500,
	fallbackMessage?: string
): { statusCode: number; statusMessage: string; cause?: any } {
	const originalErrorInfo = extractOriginalErrorInfo(error);

	if (originalErrorInfo?.statusCode) {
		return {
			statusCode: originalErrorInfo.statusCode,
			statusMessage:
				originalErrorInfo.statusMessage || fallbackMessage || error.message,
			cause: error.originalErrorObject
		};
	}

	return {
		statusCode: fallbackStatusCode,
		statusMessage: fallbackMessage || error.message,
		cause: error.originalErrorObject
	};
}

/**
 * Handles repository errors with automatic status code preservation
 * @param error - The ApplicationError from repository layer
 * @param defaultMessage - Default message to use if none provided
 * @param defaultStatusCode - Default status code to use if none found (default: 500)
 * @throws H3Error with preserved status code and message from original API
 */
function handleRepositoryError(
	error: ApplicationError,
	defaultMessage: string,
	defaultStatusCode: number = 500
): never {
	const errorInfo = createErrorFromRepositoryError(
		error,
		defaultStatusCode,
		defaultMessage
	);

	throw createError({
		statusCode: errorInfo.statusCode,
		statusMessage: errorInfo.statusMessage,
		cause: errorInfo.cause
	});
}

/**
 * Generic wrapper for repository operations that handles errors automatically
 * @param repositoryOperation - Function that returns a repository Result tuple
 * @param errorMessage - Message to use if the operation fails
 * @param errorStatusCode - Status code to use if operation fails and no original code exists
 * @returns The successful result data
 * @throws H3Error with preserved status code and message from original API
 */
async function executeRepositoryOperation<T>(
	repositoryOperation: () => Promise<[T, null] | [null, ApplicationError]>,
	errorMessage: string,
	errorStatusCode: number = 500
): Promise<T> {
	const [result, error] = await repositoryOperation();

	if (error) {
		handleRepositoryError(error, errorMessage, errorStatusCode);
	}

	return result!;
}

/**
 * Generic error handler for catch blocks that preserves original error info
 * @param error - The caught error
 * @param defaultMessage - Default message for unknown errors
 * @param defaultStatusCode - Default status code (default: 500)
 * @throws H3Error with preserved status code and message from original error
 */
function handleGenericEndpointError(
	error: unknown,
	defaultMessage: string,
	defaultStatusCode: number = 500
): never {
	// If it's already an H3Error, re-throw it
	if (error && typeof error === 'object' && 'statusCode' in error) {
		throw error;
	}

	// If it's an ApplicationError from repository, delegate to repository error handler
	if (isApplicationError(error)) {
		handleRepositoryError(error, defaultMessage, defaultStatusCode);
	}

	// Extract error info from $fetch errors
	const errorInfo = createOriginalErrorObject(error);

	throw createError({
		statusCode: errorInfo.statusCode || defaultStatusCode,
		statusMessage: errorInfo.statusMessage || defaultMessage,
		cause: error
	});
}

/**
 * Handles repository errors with special logic for 404 cases
 * @param error - ApplicationError from repository
 * @param entityName - Name of the entity for error messages
 * @param identifier - Identifier that was not found
 * @param defaultErrorMessage - Default error message for non-404 errors
 * @throws H3Error with appropriate status code
 */
function handleRepositoryErrorWithNotFound(
	error: ApplicationError | null,
	entityName: string,
	identifier: string,
	defaultErrorMessage: string
): void {
	if (!error) return;

	const originalErrorInfo = extractOriginalErrorInfo(error);
	const statusCode = originalErrorInfo?.statusCode;

	if (statusCode === 404) {
		throw createError({
			statusCode: 404,
			statusMessage: `${entityName} with identifier '${identifier}' not found`
		});
	}

	// For other errors, use the existing error creation logic to ensure consistency
	const errorInfo = createErrorFromRepositoryError(
		error,
		500,
		defaultErrorMessage
	);

	throw createError({
		statusCode: errorInfo.statusCode,
		statusMessage: errorInfo.statusMessage,
		cause: errorInfo.cause
	});
}

/**
 * Handles repository errors with special logic for 404 cases and ensures entity exists
 * @param result - The result from repository operation
 * @param error - ApplicationError from repository
 * @param entityName - Name of the entity for error messages
 * @param identifier - Identifier that was not found
 * @param defaultErrorMessage - Default error message for non-404 errors
 * @returns The result if it exists
 * @throws H3Error with appropriate status code
 */
function handleRepositoryResultWithNotFound<T>(
	result: T | null,
	error: ApplicationError | null,
	entityName: string,
	identifier: string,
	defaultErrorMessage: string
): T {
	// First handle any repository errors using existing logic
	if (error) {
		handleRepositoryErrorWithNotFound(
			error,
			entityName,
			identifier,
			defaultErrorMessage
		);
	}

	// If no error but result is null/undefined, it's a not found case
	if (!result) {
		throw createError({
			statusCode: 404,
			statusMessage: `${entityName} with identifier '${identifier}' not found`
		});
	}

	return result;
}

/**
 * Safe wrapper for repository operations that handles errors and ensures result exists
 * @param repositoryOperation - Function that returns a repository Result tuple
 * @param entityName - Name of the entity for error messages
 * @param identifier - Identifier being searched
 * @param defaultErrorMessage - Default error message for non-404 errors
 * @returns The successful result data
 * @throws H3Error with preserved status code and message from original API
 */
async function executeRepositoryOperationWithNotFound<T>(
	repositoryOperation: () => Promise<[T, null] | [null, ApplicationError]>,
	entityName: string,
	identifier: string,
	defaultErrorMessage: string
): Promise<T> {
	const [result, error] = await repositoryOperation();

	return handleRepositoryResultWithNotFound(
		result,
		error,
		entityName,
		identifier,
		defaultErrorMessage
	);
}

/**
 * Safe wrapper for endpoint main logic that handles all error cases
 * @param operation - The main endpoint logic
 * @param errorMessage - Message to use for unknown errors
 * @returns The result of the operation
 * @throws H3Error with preserved status codes
 */
async function safeEndpointWrapper<T>(
	operation: () => Promise<T>,
	errorMessage: string
): Promise<T> {
	try {
		return await operation();
	} catch (error) {
		handleGenericEndpointError(error, errorMessage);
	}
}

export {
	isFetchError,
	isApplicationError,
	extractFetchErrorInfo,
	createOriginalErrorObject,
	extractOriginalErrorInfo,
	getErrorStatusCode,
	createErrorFromRepositoryError,
	handleRepositoryError,
	executeRepositoryOperation,
	handleGenericEndpointError,
	handleRepositoryErrorWithNotFound,
	handleRepositoryResultWithNotFound,
	executeRepositoryOperationWithNotFound,
	safeEndpointWrapper
};

export type { FetchError, FetchErrorResponse, FetchErrorData };
